// src/components/admin/IndustryManagement.tsx - Redesigned to Match Admin Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Paper,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  Container,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Description as TemplateIcon,
  CheckCircle as ApproveIcon,
  HideSource as HideIcon,
  Delete as DeleteIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as industryApi from '../../services/industry';
import type { Industry, SubIndustry } from '../../services/industry';

// @ts-ignore - xlsx types issue
const XLSX = require('xlsx');

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface StatCard {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error';
  description: string;
}

const IndustryManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showHidden, setShowHidden] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  const categories = ['Localization', 'Service & Infrastructure', 'Mature Industries'];

  // Fetch industries from API
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoading(true);
        const data = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
        setIndustries(data);
      } catch (error) {
        console.error('Error fetching industries:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load industries',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  // Filter industries based on search and category
  const filteredIndustries = industries.filter(industry => {
    const matchesSearch = searchTerm === '' ||
      industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      industry.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      industry.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = filterCategory === 'all' || industry.category === filterCategory;

    const matchesVisibility = showHidden || industry.isActive !== false;

    return matchesSearch && matchesCategory && matchesVisibility;
  });

  const handleIndustryClick = (industryId: string) => {
    navigate(`/admin/industry-details/${industryId}`);
  };

  const handleToggleActive = async (industryId: string) => {
    try {
      const industry = industries.find(ind => ind.id === industryId);
      if (!industry) return;

      // Update via API
      await industryApi.updateIndustry(industryId, {
        isActive: !industry.isActive
      } as any);

      // Update local state
      setIndustries(prev =>
        prev.map(ind =>
          ind.id === industryId
            ? { ...ind, isActive: !ind.isActive }
            : ind
        )
      );

      setSnackbar({
        open: true,
        message: 'Industry status updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating industry status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update industry status',
        severity: 'error'
      });
    }
  };

  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return null;
  };

  const user = getUserInfo();
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Get status badge properties
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'draft':
        return { label: 'Draft', color: 'default' as const, bgcolor: theme.palette.grey[200], textColor: theme.palette.text.secondary };
      case 'pending_review':
        return { label: 'Pending Review', color: 'warning' as const, bgcolor: alpha(theme.palette.warning.main, 0.1), textColor: theme.palette.warning.main };
      case 'published':
        return { label: 'Published', color: 'success' as const, bgcolor: alpha(theme.palette.success.main, 0.1), textColor: theme.palette.success.main };
      case 'hidden':
        return { label: 'Hidden', color: 'error' as const, bgcolor: alpha(theme.palette.error.main, 0.1), textColor: theme.palette.error.main };
      default:
        return { label: 'Published', color: 'success' as const, bgcolor: alpha(theme.palette.success.main, 0.1), textColor: theme.palette.success.main };
    }
  };

  // Handle status actions
  const handleSubmitForReview = async (industryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await industryApi.submitIndustryForReview(industryId);
      const data = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
      setIndustries(data);
      setSnackbar({ open: true, message: 'Industry submitted for review', severity: 'success' });
    } catch (error) {
      console.error('Error submitting industry:', error);
      setSnackbar({ open: true, message: 'Failed to submit industry', severity: 'error' });
    }
  };

  const handleApprove = async (industryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await industryApi.approveIndustry(industryId);
      const data = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
      setIndustries(data);
      setSnackbar({ open: true, message: 'Industry approved and published', severity: 'success' });
    } catch (error) {
      console.error('Error approving industry:', error);
      setSnackbar({ open: true, message: 'Failed to approve industry', severity: 'error' });
    }
  };

  const handleHide = async (industryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to hide this industry?')) return;

    try {
      await industryApi.hideIndustry(industryId);
      const data = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
      setIndustries(data);
      setSnackbar({ open: true, message: 'Industry hidden', severity: 'success' });
    } catch (error) {
      console.error('Error hiding industry:', error);
      setSnackbar({ open: true, message: 'Failed to hide industry', severity: 'error' });
    }
  };

  const handlePermanentDelete = async (industryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this industry? This action cannot be undone!')) return;

    try {
      await industryApi.permanentlyDeleteIndustry(industryId);
      const data = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
      setIndustries(data);
      setSnackbar({ open: true, message: 'Industry permanently deleted', severity: 'success' });
    } catch (error) {
      console.error('Error deleting industry:', error);
      setSnackbar({ open: true, message: 'Failed to delete industry', severity: 'error' });
    }
  };

  const getCategoryColor = (category: string): "primary" | "secondary" | "info" | "default" => {
    switch (category) {
      case 'Localization': return 'primary';
      case 'Service & Infrastructure': return 'secondary';
      case 'Mature Industries': return 'info';
      default: return 'default';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Define FULLY EXPANDED template - EVERY field gets its own column (NO JSON)
      const industryTemplate = [
        {
          // ===== BASIC INDUSTRY INFORMATION =====
          'Industry Name': '',
          'Category': 'Localization',
          'Icon': '',
          'Has Sub-Industries': 'TRUE',
          'Is Active': 'TRUE',
          'Description': '',
          'Summary': '',
          'Growth Rate (%)': '',
          'Market Size': '',
          'Investment Opportunities': '',

          // ===== KEY METRICS =====
          'Key Metrics - Employment': '',
          'Key Metrics - Exports': '',
          'Key Metrics - Companies': '',

          // ===== GROWTH DATA =====
          'Growth Data 2020': '',
          'Growth Data 2021': '',
          'Growth Data 2022': '',
          'Growth Data 2023': '',
          'Growth Data 2024': '',

          // ===== TABS - MARKET OVERVIEW =====
          'Tab Market Overview - Market Potential 1': '',
          'Tab Market Overview - Market Potential 2': '',
          'Tab Market Overview - Market Potential 3': '',
          'Tab Market Overview - Critical Gap 1': '',
          'Tab Market Overview - Critical Gap 2': '',
          'Tab Market Overview - Critical Gap 3': '',
          'Tab Market Overview - Critical Strength 1': '',
          'Tab Market Overview - Critical Strength 2': '',
          'Tab Market Overview - Critical Strength 3': '',
          'Tab Market Overview - Gap Blocking Growth 1': '',
          'Tab Market Overview - Gap Blocking Growth 2': '',
          'Tab Market Overview - Gap Blocking Growth 3': '',
          'Tab Market Overview - Investment Shortfall 1': '',
          'Tab Market Overview - Investment Shortfall 2': '',
          'Tab Market Overview - Bottom Line Text': '',
          'Tab Market Overview - Bottom Line Point 1': '',
          'Tab Market Overview - Bottom Line Point 2': '',
          'Tab Market Overview - Bottom Line Point 3': '',
          'Tab Market Overview - National Strategy Title': '',
          'Tab Market Overview - National Strategy Published': '',
          'Tab Market Overview - National Strategy Vision': '',
          'Tab Market Overview - National Strategy Focus Area 1': '',
          'Tab Market Overview - National Strategy Focus Area 2': '',
          'Tab Market Overview - National Strategy Focus Area 3': '',
          'Tab Market Overview - National Strategy Pillar 1': '',
          'Tab Market Overview - National Strategy Pillar 2': '',
          'Tab Market Overview - National Strategy Pillar 3': '',
          'Tab Market Overview - National Strategy Pillar 4': '',
          'Tab Market Overview - National Strategy Pillar 5': '',
          'Tab Market Overview - National Strategy Why It Matters 1': '',
          'Tab Market Overview - National Strategy Why It Matters 2': '',
          'Tab Market Overview - National Strategy Why It Matters 3': '',
          'Tab Market Overview - National Strategy Document Link': '',

          // ===== TABS - LEGAL & INFRASTRUCTURE - LEGAL =====
          'Tab Legal - Regulatory Link Group 1 Title': '',
          'Tab Legal - Regulatory Link Group 1 Item 1 Name': '',
          'Tab Legal - Regulatory Link Group 1 Item 1 Link': '',
          'Tab Legal - Regulatory Link Group 1 Item 2 Name': '',
          'Tab Legal - Regulatory Link Group 1 Item 2 Link': '',
          'Tab Legal - Regulatory Link Group 2 Title': '',
          'Tab Legal - Regulatory Link Group 2 Item 1 Name': '',
          'Tab Legal - Regulatory Link Group 2 Item 1 Link': '',
          'Tab Legal - Regulatory Link Group 2 Item 2 Name': '',
          'Tab Legal - Regulatory Link Group 2 Item 2 Link': '',
          'Tab Legal - Regulatory Link Group 3 Title': '',
          'Tab Legal - Regulatory Link Group 3 Item 1 Name': '',
          'Tab Legal - Regulatory Link Group 3 Item 1 Link': '',
          'Tab Legal - Regulatory Link Group 3 Item 2 Name': '',
          'Tab Legal - Regulatory Link Group 3 Item 2 Link': '',
          'Tab Legal - Regulatory Link Group 4 Title': '',
          'Tab Legal - Regulatory Link Group 4 Item 1 Name': '',
          'Tab Legal - Regulatory Link Group 4 Item 1 Link': '',
          'Tab Legal - SFDA ML4 Title': '',
          'Tab Legal - SFDA ML4 Description': '',
          'Tab Legal - SFDA ML4 Impact 1': '',
          'Tab Legal - SFDA ML4 Impact 2': '',
          'Tab Legal - SFDA ML4 Impact 3': '',
          'Tab Legal - SFDA ML4 Source': '',
          'Tab Legal - Note 1': '',
          'Tab Legal - Note 2': '',
          'Tab Legal - Foundation 1': '',
          'Tab Legal - Foundation 2': '',
          'Tab Legal - Foundation 3': '',
          'Tab Legal - Foundation 4': '',

          // ===== TABS - LEGAL & INFRASTRUCTURE - INFRASTRUCTURE =====
          'Tab Infrastructure - Physical 1': '',
          'Tab Infrastructure - Physical 2': '',
          'Tab Infrastructure - Physical 3': '',
          'Tab Infrastructure - Key Conflict': '',
          'Tab Infrastructure - Digital 1': '',
          'Tab Infrastructure - Digital 2': '',
          'Tab Infrastructure - Priorities Intro': '',
          'Tab Infrastructure - Priority Item 1': '',
          'Tab Infrastructure - Priority Item 2': '',
          'Tab Infrastructure - Priority Item 3': '',

          // ===== TABS - TRENDS =====
          'Tab Trends - Trend 1 Name': '',
          'Tab Trends - Trend 1 Point 1': '',
          'Tab Trends - Trend 1 Point 2': '',
          'Tab Trends - Trend 2 Name': '',
          'Tab Trends - Trend 2 Point 1': '',
          'Tab Trends - Trend 2 Point 2': '',
          'Tab Trends - Trend 3 Name': '',
          'Tab Trends - Trend 3 Point 1': '',
          'Tab Trends - Trend 3 Point 2': '',
          'Tab Trends - Trend 4 Name': '',
          'Tab Trends - Trend 4 Point 1': '',
          'Tab Trends - Trend 4 Point 2': '',
          'Tab Trends - Trend 5 Name': '',
          'Tab Trends - Trend 5 Point 1': '',
          'Tab Trends - Trend 5 Point 2': '',
          'Tab Trends - Trend 6 Name': '',
          'Tab Trends - Trend 6 Point 1': '',
          'Tab Trends - Trend 6 Point 2': '',
          'Tab Trends - Where To Start 1': '',
          'Tab Trends - Where To Start 2': '',
          'Tab Trends - Where To Start 3': '',

          // ===== TABS - INCENTIVES =====
          'Tab Incentives - Section 1 Title': '',
          'Tab Incentives - Section 1 Item 1 Subtitle': '',
          'Tab Incentives - Section 1 Item 1 Point 1': '',
          'Tab Incentives - Section 1 Item 1 Point 2': '',
          'Tab Incentives - Section 1 Item 1 Point 3': '',
          'Tab Incentives - Section 1 Item 2 Subtitle': '',
          'Tab Incentives - Section 1 Item 2 Point 1': '',
          'Tab Incentives - Section 1 Item 2 Point 2': '',
          'Tab Incentives - Section 2 Title': '',
          'Tab Incentives - Section 2 Point 1': '',
          'Tab Incentives - Section 2 Point 2': '',
          'Tab Incentives - Section 2 Point 3': '',
          'Tab Incentives - Section 3 Title': '',
          'Tab Incentives - Section 3 Point 1': '',
          'Tab Incentives - Section 3 Point 2': '',
          'Tab Incentives - Section 4 Title': '',
          'Tab Incentives - Section 4 Point 1': '',
          'Tab Incentives - Section 4 Point 2': '',
          'Tab Incentives - Section 5 Title': '',
          'Tab Incentives - Section 5 Point 1': '',
          'Tab Incentives - Section 5 Point 2': '',

          // ===== VALUE CHAIN - IMPORT =====
          'Value Chain Import - Title': '',
          'Value Chain Import - Top Category 1 Name': '',
          'Value Chain Import - Top Category 1 Share': '',
          'Value Chain Import - Top Category 1 Suppliers': '',
          'Value Chain Import - Top Category 2 Name': '',
          'Value Chain Import - Top Category 2 Share': '',
          'Value Chain Import - Top Category 2 Suppliers': '',
          'Value Chain Import - Top Category 3 Name': '',
          'Value Chain Import - Top Category 3 Share': '',
          'Value Chain Import - Top Category 3 Suppliers': '',
          'Value Chain Import - Top Category 4 Name': '',
          'Value Chain Import - Top Category 4 Share': '',
          'Value Chain Import - Top Category 4 Suppliers': '',
          'Value Chain Import - Top Category 5 Name': '',
          'Value Chain Import - Top Category 5 Share': '',
          'Value Chain Import - Top Category 5 Suppliers': '',
          'Value Chain Import - Dominant Supplier 1': '',
          'Value Chain Import - Dominant Supplier 2': '',
          'Value Chain Import - Dominant Supplier 3': '',
          'Value Chain Import - Critical Dependency 1': '',
          'Value Chain Import - Critical Dependency 2': '',
          'Value Chain Import - Import Reduction Plan Text': '',
          'Value Chain Import - Import Reduction Point 1': '',
          'Value Chain Import - Import Reduction Point 2': '',

          // ===== VALUE CHAIN - RESEARCH & DISCOVERY =====
          'Value Chain Research - Title': '',
          'Value Chain Research - Summary': '',
          'Value Chain Research - Product/Service Needed 1': '',
          'Value Chain Research - Product/Service Needed 2': '',
          'Value Chain Research - Key Player 1': '',
          'Value Chain Research - Key Player 2': '',
          'Value Chain Research - Trends': '',
          'Value Chain Research - Challenges': '',
          'Value Chain Research - Risk Rating': '',
          'Value Chain Research - Policy Recommendation 1': '',
          'Value Chain Research - Policy Recommendation 2': '',

          // ===== VALUE CHAIN - DEVELOPMENT =====
          'Value Chain Development - Title': '',
          'Value Chain Development - Summary': '',
          'Value Chain Development - Product/Service Needed 1': '',
          'Value Chain Development - Product/Service Needed 2': '',
          'Value Chain Development - Key Player 1': '',
          'Value Chain Development - Key Player 2': '',
          'Value Chain Development - Trends': '',
          'Value Chain Development - Challenges': '',
          'Value Chain Development - Risk Rating': '',
          'Value Chain Development - Policy Recommendation 1': '',
          'Value Chain Development - Policy Recommendation 2': '',

          // ===== VALUE CHAIN - MANUFACTURING =====
          'Value Chain Manufacturing - Title': '',
          'Value Chain Manufacturing - Summary': '',
          'Value Chain Manufacturing - Product/Service Needed 1': '',
          'Value Chain Manufacturing - Product/Service Needed 2': '',
          'Value Chain Manufacturing - Key Player 1': '',
          'Value Chain Manufacturing - Key Player 2': '',
          'Value Chain Manufacturing - Trends': '',
          'Value Chain Manufacturing - Challenges': '',
          'Value Chain Manufacturing - Risk Rating': '',
          'Value Chain Manufacturing - Policy Recommendation 1': '',
          'Value Chain Manufacturing - Policy Recommendation 2': '',

          // ===== VALUE CHAIN - COMMERCIALIZATION =====
          'Value Chain Commercialization - Title': '',
          'Value Chain Commercialization - Summary': '',
          'Value Chain Commercialization - Product/Service Needed 1': '',
          'Value Chain Commercialization - Product/Service Needed 2': '',
          'Value Chain Commercialization - Key Player 1': '',
          'Value Chain Commercialization - Key Player 2': '',
          'Value Chain Commercialization - Trends': '',
          'Value Chain Commercialization - Challenges': '',
          'Value Chain Commercialization - Risk Rating': '',
          'Value Chain Commercialization - Policy Recommendation 1': '',
          'Value Chain Commercialization - Policy Recommendation 2': '',

          // ===== SUPPORT ECOSYSTEM - GENERAL =====
          'Support Ecosystem - Summary': '',
          'Support Ecosystem - Product/Service Needed 1': '',
          'Support Ecosystem - Product/Service Needed 2': '',
          'Support Ecosystem - Key Player 1': '',
          'Support Ecosystem - Key Player 2': '',
          'Support Ecosystem - Trends': '',
          'Support Ecosystem - Challenges': '',
          'Support Ecosystem - Risk Rating': '',
          'Support Ecosystem - Policy Recommendation 1': '',
          'Support Ecosystem - Policy Recommendation 2': '',

          // ===== SUPPORT ECOSYSTEM - TALENT =====
          'Support Ecosystem Talent - Title': '',
          'Support Ecosystem Talent - Critical Shortage 1': '',
          'Support Ecosystem Talent - Critical Shortage 2': '',
          'Support Ecosystem Talent - Critical Shortage 3': '',
          'Support Ecosystem Talent - University 1 Name': '',
          'Support Ecosystem Talent - University 1 Program 1': '',
          'Support Ecosystem Talent - University 1 Program 2': '',
          'Support Ecosystem Talent - University 2 Name': '',
          'Support Ecosystem Talent - University 2 Program 1': '',
          'Support Ecosystem Talent - University 2 Program 2': '',
          'Support Ecosystem Talent - University 3 Name': '',
          'Support Ecosystem Talent - University 3 Program 1': '',
          'Support Ecosystem Talent - University 3 Program 2': '',
          'Support Ecosystem Talent - Government Initiative 1': '',
          'Support Ecosystem Talent - Government Initiative 2': '',
          'Support Ecosystem Talent - Trend/Challenge 1': '',
          'Support Ecosystem Talent - Trend/Challenge 2': '',

          // ===== SUPPORT ECOSYSTEM - FUNDING =====
          'Support Ecosystem Funding - Title': '',
          'Support Ecosystem Funding - Investment Landscape Summary': '',
          'Support Ecosystem Funding - Key Metric 1': '',
          'Support Ecosystem Funding - Key Metric 2': '',
          'Support Ecosystem Funding - Key Metric 3': '',
          'Support Ecosystem Funding - Source Group 1 Name': '',
          'Support Ecosystem Funding - Source Group 1 Entity 1': '',
          'Support Ecosystem Funding - Source Group 1 Entity 2': '',
          'Support Ecosystem Funding - Source Group 1 Entity 3': '',
          'Support Ecosystem Funding - Source Group 2 Name': '',
          'Support Ecosystem Funding - Source Group 2 Entity 1': '',
          'Support Ecosystem Funding - Source Group 2 Entity 2': '',
          'Support Ecosystem Funding - Source Group 2 Entity 3': '',
          'Support Ecosystem Funding - Trend/Gap 1': '',
          'Support Ecosystem Funding - Trend/Gap 2': '',
          'Support Ecosystem Funding - Trend/Gap 3': '',

          // ===== SUPPORT ECOSYSTEM - SUPPLY CHAIN =====
          'Support Ecosystem Supply Chain - Title': '',
          'Support Ecosystem Supply Chain - Critical Gap 1': '',
          'Support Ecosystem Supply Chain - Critical Gap 2': '',
          'Support Ecosystem Supply Chain - Critical Gap 3': '',
          'Support Ecosystem Supply Chain - Localization Opp 1 Category': '',
          'Support Ecosystem Supply Chain - Localization Opp 1 Details': '',
          'Support Ecosystem Supply Chain - Localization Opp 1 Target Year': '',
          'Support Ecosystem Supply Chain - Localization Opp 2 Category': '',
          'Support Ecosystem Supply Chain - Localization Opp 2 Details': '',
          'Support Ecosystem Supply Chain - Localization Opp 2 Target Year': '',
          'Support Ecosystem Supply Chain - Localization Opp 3 Category': '',
          'Support Ecosystem Supply Chain - Localization Opp 3 Details': '',
          'Support Ecosystem Supply Chain - Localization Opp 3 Target Year': '',
          'Support Ecosystem Supply Chain - Highest Risk 1': '',
          'Support Ecosystem Supply Chain - Highest Risk 2': '',
          'Support Ecosystem Supply Chain - Emerging Risk 1': '',
          'Support Ecosystem Supply Chain - Emerging Risk 2': '',
          'Support Ecosystem Supply Chain - Strategic Initiative 1': '',
          'Support Ecosystem Supply Chain - Strategic Initiative 2': '',
          'Support Ecosystem Supply Chain - Strategic Initiative 3': '',

          // ===== SUB-INDUSTRY 1 - BASIC INFO =====
          'Sub-Industry 1 Name': '',
          'Sub-Industry 1 Description': '',
          'Sub-Industry 1 Summary': '',
          'Sub-Industry 1 Growth Rate (%)': '',
          'Sub-Industry 1 Market Size': '',
          'Sub-Industry 1 Investment Opportunities': '',
          'Sub-Industry 1 Key Metrics - Employment': '',
          'Sub-Industry 1 Key Metrics - Exports': '',
          'Sub-Industry 1 Key Metrics - Companies': '',
          'Sub-Industry 1 Growth Data 2020': '',
          'Sub-Industry 1 Growth Data 2021': '',
          'Sub-Industry 1 Growth Data 2022': '',
          'Sub-Industry 1 Growth Data 2023': '',
          'Sub-Industry 1 Growth Data 2024': '',

          // NOTE: Sub-industry tabs/value chain/support ecosystem would follow the same pattern
          // Add more columns as needed for additional sub-industries (Sub-Industry 2, 3, etc.)
        }
      ];

      // Create comprehensive instructions sheet
      const instructions = [
        { Section: 'OVERVIEW', Field: 'Purpose', Description: 'This template allows you to bulk import comprehensive industry data including market overview, tabs, value chains, and support ecosystems' },
        { Section: '', Field: '', Description: '' },
        { Section: 'REQUIRED FIELDS', Field: 'Industry Name', Description: 'Name of the industry (REQUIRED)' },
        { Section: 'REQUIRED FIELDS', Field: 'Category', Description: 'One of: Localization, Service & Infrastructure, Mature Industries (REQUIRED)' },
        { Section: 'REQUIRED FIELDS', Field: 'Has Sub-Industries', Description: 'TRUE or FALSE (REQUIRED)' },
        { Section: 'REQUIRED FIELDS', Field: 'Is Active', Description: 'TRUE or FALSE - visibility status (REQUIRED, default TRUE)' },
        { Section: '', Field: '', Description: '' },
        { Section: 'BASIC INFO', Field: 'Icon', Description: 'Icon name (e.g., "PillIcon", "CarIcon")' },
        { Section: 'BASIC INFO', Field: 'Description', Description: 'Brief tagline (e.g., "56 Plants, SR7B Investment")' },
        { Section: 'BASIC INFO', Field: 'Summary', Description: 'Comprehensive industry summary paragraph' },
        { Section: 'BASIC INFO', Field: 'Growth Rate (%)', Description: 'Annual growth rate as number (e.g., 12.5)' },
        { Section: 'BASIC INFO', Field: 'Market Size', Description: 'Market size (e.g., "2.8B", "$500M")' },
        { Section: 'BASIC INFO', Field: 'Investment Opportunities', Description: 'Number of opportunities (e.g., 45)' },
        { Section: '', Field: '', Description: '' },
        { Section: 'KEY METRICS', Field: 'Key Metrics - Employment', Description: 'Employment figures (e.g., "25,000+")' },
        { Section: 'KEY METRICS', Field: 'Key Metrics - Exports', Description: 'Export value (e.g., "$1.2B")' },
        { Section: 'KEY METRICS', Field: 'Key Metrics - Companies', Description: 'Number of companies (e.g., 180)' },
        { Section: 'KEY METRICS', Field: 'Key Metrics (Additional JSON)', Description: 'Additional metrics as JSON: {"metric": "value"}' },
        { Section: '', Field: '', Description: '' },
        { Section: 'GROWTH DATA', Field: 'Growth Data 20XX', Description: 'Value for specific year (e.g., 1.8, 2.1, 2.4)' },
        { Section: '', Field: '', Description: '' },
        { Section: 'SEPARATORS', Field: 'Pipe-separated (|)', Description: 'Use | to separate multiple bullet points or list items' },
        { Section: 'SEPARATORS', Field: 'Comma-separated', Description: 'Use , for simple lists of items' },
        { Section: 'SEPARATORS', Field: 'JSON Format', Description: 'Use valid JSON for complex nested structures' },
        { Section: '', Field: '', Description: '' },
        { Section: 'TABS', Field: 'Market Overview', Description: 'Market potential, gaps, strengths, strategy details' },
        { Section: 'TABS', Field: 'Legal & Infrastructure', Description: 'Regulatory links, SFDA info, infrastructure details' },
        { Section: 'TABS', Field: 'Trends', Description: 'Industry trends as JSON array' },
        { Section: 'TABS', Field: 'Incentives', Description: 'Investment incentives as JSON structure' },
        { Section: '', Field: '', Description: '' },
        { Section: 'VALUE CHAIN', Field: 'Import', Description: 'Top import categories, suppliers, dependencies' },
        { Section: 'VALUE CHAIN', Field: 'Research & Discovery', Description: 'Summary, products needed, key players, trends, challenges' },
        { Section: 'VALUE CHAIN', Field: 'Development', Description: 'Clinical trials, approvals, development infrastructure' },
        { Section: 'VALUE CHAIN', Field: 'Manufacturing', Description: 'Manufacturing capabilities, facilities, challenges' },
        { Section: 'VALUE CHAIN', Field: 'Commercialization', Description: 'Market access, distribution, commercialization strategy' },
        { Section: '', Field: '', Description: '' },
        { Section: 'SUPPORT ECOSYSTEM', Field: 'General', Description: 'Overall ecosystem summary and key elements' },
        { Section: 'SUPPORT ECOSYSTEM', Field: 'Talent', Description: 'Workforce gaps, university programs, initiatives' },
        { Section: 'SUPPORT ECOSYSTEM', Field: 'Funding', Description: 'Investment landscape, funding sources, gaps' },
        { Section: 'SUPPORT ECOSYSTEM', Field: 'Supply Chain', Description: 'Supply chain gaps, opportunities, risks' },
        { Section: '', Field: '', Description: '' },
        { Section: 'SUB-INDUSTRIES', Field: 'Numbering', Description: 'Use "Sub-Industry 1", "Sub-Industry 2", etc. for multiple sub-industries' },
        { Section: 'SUB-INDUSTRIES', Field: 'Complete Data', Description: 'Each sub-industry can have its own tabs, value chain, and ecosystem (as JSON)' },
        { Section: '', Field: '', Description: '' },
        { Section: 'TIPS', Field: 'See Example Sheet', Description: 'Review the "Example - Pharmaceutical" sheet for complete data structure' },
        { Section: 'TIPS', Field: 'Start Simple', Description: 'Begin with basic fields, then add detailed tabs/value chains' },
        { Section: 'TIPS', Field: 'Validate JSON', Description: 'Use a JSON validator before uploading complex JSON fields' }
      ];

      // Create professionally branded workbook
      const wb = XLSX.utils.book_new();

      // Add instructions sheet with professional styling
      const wsInstructions = XLSX.utils.json_to_sheet(instructions);
      wsInstructions['!cols'] = [{ wch: 22 }, { wch: 35 }, { wch: 80 }];

      // Style the instructions header row
      const instructionsRange = XLSX.utils.decode_range(wsInstructions['!ref'] || 'A1');
      for (let col = instructionsRange.s.c; col <= instructionsRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!wsInstructions[cellAddress]) continue;
        wsInstructions[cellAddress].s = {
          font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "2563EB" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      XLSX.utils.book_append_sheet(wb, wsInstructions, '📖 Instructions');

      // Add template sheet with professional column headers
      const wsTemplate = XLSX.utils.json_to_sheet(industryTemplate);
      const templateCols = industryTemplate[0] ? Object.keys(industryTemplate[0]) : [];
      wsTemplate['!cols'] = templateCols.map((colName) => {
        // Wider columns for long text fields
        if (colName.includes('Summary') || colName.includes('Description') || colName.includes('Vision')) {
          return { wch: 50 };
        } else if (colName.includes('Point') || colName.includes('Item')) {
          return { wch: 45 };
        }
        return { wch: 35 };
      });

      // Style template header row with brand colors
      const templateRange = XLSX.utils.decode_range(wsTemplate['!ref'] || 'A1');
      for (let col = templateRange.s.c; col <= templateRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!wsTemplate[cellAddress]) continue;

        const colName = templateCols[col] || '';
        let headerColor = "2563EB"; // Default blue

        // Color code by section
        if (colName.startsWith('Tab Market Overview')) headerColor = "059669"; // Green
        else if (colName.startsWith('Tab Legal') || colName.startsWith('Tab Infrastructure')) headerColor = "DC2626"; // Red
        else if (colName.startsWith('Tab Trends')) headerColor = "7C3AED"; // Purple
        else if (colName.startsWith('Tab Incentives')) headerColor = "EA580C"; // Orange
        else if (colName.startsWith('Value Chain')) headerColor = "0891B2"; // Cyan
        else if (colName.startsWith('Support Ecosystem')) headerColor = "DB2777"; // Pink
        else if (colName.startsWith('Sub-Industry')) headerColor = "65A30D"; // Lime

        wsTemplate[cellAddress].s = {
          font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: headerColor } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Freeze first row
      wsTemplate['!freeze'] = { xSplit: 0, ySplit: 1 };

      XLSX.utils.book_append_sheet(wb, wsTemplate, '📝 Template - Fill Here');

      // Add COMPLETE pharmaceutical example with EVERY field filled from your data
      const examplePharm = [{
        // ===== BASIC INDUSTRY INFORMATION =====
        'Industry Name': 'Pharmaceutical Sector',
        'Category': 'Localization',
        'Icon': 'PillIcon',
        'Has Sub-Industries': 'TRUE',
        'Is Active': 'TRUE',
        'Description': '56 Plants, SR7B Investment',
        'Summary': 'The Saudi pharmaceutical market is one of the largest in the Middle East and North Africa (MENA) region, showing steady growth due to a growing population, increased healthcare spending, and a rising prevalence of chronic diseases. Government initiatives aim to strengthen local pharmaceutical manufacturing and reduce dependence on imports. The market is witnessing advancements in biotechnology and digital health technologies and creating opportunities for innovation. By 2034, the market is projected to reach a valuation of USD 16.79 billion, growing at an annual growth rate (CAGR) of 5.20%.',
        'Growth Rate (%)': '12.5',
        'Market Size': '2.8B',
        'Investment Opportunities': '45',

        // ===== KEY METRICS =====
        'Key Metrics - Employment': '25,000+',
        'Key Metrics - Exports': '$1.2B',
        'Key Metrics - Companies': '180',

        // ===== GROWTH DATA =====
        'Growth Data 2020': '1.8',
        'Growth Data 2021': '2.1',
        'Growth Data 2022': '2.4',
        'Growth Data 2023': '2.6',
        'Growth Data 2024': '2.8',

        // ===== TABS - MARKET OVERVIEW =====
        'Tab Market Overview - Market Potential 1': '$11.5B Saudi pharma market (2024), growing at 7% CAGR – but 70% imported (SFDA)',
        'Tab Market Overview - Market Potential 2': '$125B global halal pharma market by 2030 – Saudi could capture 15% with first-mover advantage (IMARC)',
        'Tab Market Overview - Market Potential 3': '38% adult diabetes prevalence – drives $2.7B local insulin/biologics demand (IDF 2023)',
        'Tab Market Overview - Critical Gap 1': '0 local CAR-T manufacturing despite high genetic disease burden',
        'Tab Market Overview - Critical Gap 2': '90% dependence on imported fill-finish sterile packaging',
        'Tab Market Overview - Critical Gap 3': 'Just 3% of VC funding going to biotech Series B+ rounds',
        'Tab Market Overview - Critical Strength 1': '$1B+ invested in mRNA/VLP vaccine facilities (SPARK, Vision 2030)',
        'Tab Market Overview - Critical Strength 2': '60% of Saudis carry genetic markers for recessive disorders – an untapped niche for gene therapies (KACST)',
        'Tab Market Overview - Critical Strength 3': '40% cost advantage in biosimilars vs. EU/US if localized (McKinsey)',
        'Tab Market Overview - Gap Blocking Growth 1': '90% of biomanufacturing supplies imported (media, filters, single-use systems)',
        'Tab Market Overview - Gap Blocking Growth 2': '<5% of biologics consumed are locally made (vs. 50%+ target by 2030)',
        'Tab Market Overview - Gap Blocking Growth 3': '0 approved CAR-T/Gene therapy production sites – despite 1.4x global avg. rare disease rates',
        'Tab Market Overview - Investment Shortfall 1': 'Just $300M VC funding for biotech in 2023 (vs. $4B in Singapore) (Magnitt)',
        'Tab Market Overview - Investment Shortfall 2': '3:1 ratio of grants to risk capital – stalls scaling (Saudi Venture Capital)',
        'Tab Market Overview - Bottom Line Text': 'Saudi Arabia has the demand, capital, and strategic urgency to build a biotech hub. The Kingdom must triple down on:',
        'Tab Market Overview - Bottom Line Point 1': 'Localizing high-need biologics (insulin, vaccines, mAbs)',
        'Tab Market Overview - Bottom Line Point 2': 'Leapfrogging into gene therapies (CAR-T, CRISPR for Arab genomes)',
        'Tab Market Overview - Bottom Line Point 3': 'Forcing tech transfer via off-take agreements (e.g., \'50% local content\' rules)',
        'Tab Market Overview - National Strategy Title': 'Saudi National Biotechnology Strategy – Key Highlights',
        'Tab Market Overview - National Strategy Published': 'November 2022',
        'Tab Market Overview - National Strategy Vision': 'Establish Saudi Arabia as a regional biotech leader by 2030 and a global leader by 2040',
        'Tab Market Overview - National Strategy Focus Area 1': 'Localizing 50%+ of essential biologics (vaccines, insulin, mAbs)',
        'Tab Market Overview - National Strategy Focus Area 2': 'Genomics & Precision Medicine: Leveraging Arab genome data for tailored therapies',
        'Tab Market Overview - National Strategy Focus Area 3': 'Halal Biotech Hub: Setting global standards for Islamic-compliant biologics',
        'Tab Market Overview - National Strategy Pillar 1': 'R&D: Prioritize mRNA, cell/gene therapies, and AI-driven drug discovery',
        'Tab Market Overview - National Strategy Pillar 2': 'Manufacturing: Build GMP facilities',
        'Tab Market Overview - National Strategy Pillar 3': 'Talent: Train 10,000+ specialists via KAUST/KAIMRC programs',
        'Tab Market Overview - National Strategy Pillar 4': 'Regulatory: Fast-track approvals (SFDA ML4 alignment with WHO/FDA)',
        'Tab Market Overview - National Strategy Pillar 5': 'Investment: Attract $5B+ private capital via PIF-backed ventures',
        'Tab Market Overview - National Strategy Why It Matters 1': 'Economic: Targets 1.2% GDP contribution from biotech by 2030',
        'Tab Market Overview - National Strategy Why It Matters 2': 'Health Security: Reduces reliance on imported biologics (currently 70%)',
        'Tab Market Overview - National Strategy Why It Matters 3': 'Global Positioning: Aims to rank Top 10 in biotech patents by 2035',
        'Tab Market Overview - National Strategy Document Link': 'https://www.vision2030.gov.sa/en/programs/national-biotechnology-strategy/',

        // ===== TABS - LEGAL & INFRASTRUCTURE - LEGAL =====
        'Tab Legal - Regulatory Link Group 1 Title': 'SFDA Biologics Regulations',
        'Tab Legal - Regulatory Link Group 1 Item 1 Name': 'Biological Products Regulations',
        'Tab Legal - Regulatory Link Group 1 Item 1 Link': 'https://www.sfda.gov.sa/en/regulations/biological-products',
        'Tab Legal - Regulatory Link Group 1 Item 2 Name': 'Clinical Trial Guidelines',
        'Tab Legal - Regulatory Link Group 1 Item 2 Link': 'https://www.sfda.gov.sa/en/regulations/clinical-trials',
        'Tab Legal - Regulatory Link Group 2 Title': 'Intellectual Property',
        'Tab Legal - Regulatory Link Group 2 Item 1 Name': 'Patent Box Incentives',
        'Tab Legal - Regulatory Link Group 2 Item 1 Link': 'https://www.vision2030.gov.sa/en/programs/patent-box',
        'Tab Legal - Regulatory Link Group 2 Item 2 Name': 'Pharma Patent Law 2022',
        'Tab Legal - Regulatory Link Group 2 Item 2 Link': 'https://www.saip.gov.sa/en/patent-law',
        'Tab Legal - Regulatory Link Group 3 Title': 'Manufacturing & GMP',
        'Tab Legal - Regulatory Link Group 3 Item 1 Name': 'GMP Manufacturing Standards',
        'Tab Legal - Regulatory Link Group 3 Item 1 Link': 'https://www.sfda.gov.sa/en/regulations/gmp-standards',
        'Tab Legal - Regulatory Link Group 3 Item 2 Name': 'Local Content Requirements',
        'Tab Legal - Regulatory Link Group 3 Item 2 Link': 'https://www.monshaat.gov.sa/en/local-content',
        'Tab Legal - Regulatory Link Group 4 Title': 'Halal Certification',
        'Tab Legal - Regulatory Link Group 4 Item 1 Name': 'Halal Pharma Standards',
        'Tab Legal - Regulatory Link Group 4 Item 1 Link': 'https://www.saso.gov.sa/en/halal-standards',
        'Tab Legal - SFDA ML4 Title': 'SFDA Achieves WHO Maturity Level 4 (ML4)',
        'Tab Legal - SFDA ML4 Description': 'The SFDA\'s 2023 WHO ML4 certification confirms its regulatory system meets global benchmarks for safety, efficacy, and quality control',
        'Tab Legal - SFDA ML4 Impact 1': 'Faster time-to-market for biologics (reduced approval timelines by 30%)',
        'Tab Legal - SFDA ML4 Impact 2': 'Export advantage to 100+ countries recognizing SFDA certification',
        'Tab Legal - SFDA ML4 Impact 3': 'Investor confidence – signals regulatory stability and international alignment',
        'Tab Legal - SFDA ML4 Source': 'https://www.sfda.gov.sa/en/news/78543',
        'Tab Legal - Note 1': 'SFDA 2023 reforms prioritize mRNA vaccines and cell/gene therapies with fast-track pathways',
        'Tab Legal - Note 2': 'GMP compliance mandatory for all local biomanufacturing – no exceptions for startups',
        'Tab Legal - Foundation 1': 'SFDA Modernization: Fast-track pathways for biologics (cut approval time from 18 to 12 months)',
        'Tab Legal - Foundation 2': 'IP Protection: Patent box incentives offering reduced tax rates for pharma innovations',
        'Tab Legal - Foundation 3': 'Halal Compliance: World\'s first regulatory standards for Islamic-compliant biologics manufacturing',
        'Tab Legal - Foundation 4': 'Clinical Trial Reforms: Streamlined ethics approvals and patient recruitment support',

        // ===== TABS - LEGAL & INFRASTRUCTURE - INFRASTRUCTURE =====
        'Tab Infrastructure - Physical 1': 'SPARK Biocity: $1.2B integrated biomanufacturing hub with shared GMP facilities in Riyadh',
        'Tab Infrastructure - Physical 2': 'Cold Chain Gaps: Only 3 certified -80°C storage facilities nationwide for mRNA/biologics',
        'Tab Infrastructure - Physical 3': 'Customs Bottlenecks: 14-day average import clearance for biologics (vs. 3-5 days in Singapore)',
        'Tab Infrastructure - Key Conflict': 'Cutting-edge manufacturing facilities vs. outdated customs/logistics infrastructure slowing supply chains',
        'Tab Infrastructure - Digital 1': 'Health Data Lakes: 2M+ Arab genome sequences available for research via KAIMRC partnership',
        'Tab Infrastructure - Digital 2': 'Blockchain Pilot: SFDA-led track-and-trace system for biologics launching in 2025',
        'Tab Infrastructure - Priorities Intro': 'Saudi Arabia can\'t out-innovate with 20th-century infrastructure. Three priorities:',
        'Tab Infrastructure - Priority Item 1': 'Unlock genomic data for private-sector AI-driven drug discovery partnerships',
        'Tab Infrastructure - Priority Item 2': 'Pre-approve biotech special economic zones with customs waivers for critical supplies',
        'Tab Infrastructure - Priority Item 3': 'Align SFDA digital systems with EMA/FDA for mutual recognition agreements',

        // ===== TABS - TRENDS =====
        'Tab Trends - Trend 1 Name': 'Localization Wave',
        'Tab Trends - Trend 1 Point 1': 'Priority Sectors: Vaccines (mRNA), insulin, and monoclonal antibodies targeted for local production',
        'Tab Trends - Trend 1 Point 2': 'The Play: Partner with SPARK/KAIMRC for co-development and tech transfer agreements',
        'Tab Trends - Trend 2 Name': 'Arab Genome Gold Rush',
        'Tab Trends - Trend 2 Point 1': '60% of Saudis carry genetic markers for diabetes and recessive disorders – untapped therapeutic niche',
        'Tab Trends - Trend 2 Point 2': 'The Opportunity: Develop Arab-specific diagnostics and AI-powered genomic medicine tools',
        'Tab Trends - Trend 3 Name': 'Halal Biotech Leadership',
        'Tab Trends - Trend 3 Point 1': '$125B global halal pharma market by 2030 – Saudi Arabia positioning as certification hub',
        'Tab Trends - Trend 3 Point 2': 'First-mover advantage: Set global standards for Islamic-compliant biologics manufacturing',
        'Tab Trends - Trend 4 Name': 'PIF-Backed Mega Investments',
        'Tab Trends - Trend 4 Point 1': '$1B+ committed to mRNA vaccine facilities and biosimilar plants through SPARK initiative',
        'Tab Trends - Trend 4 Point 2': '$500M PIF Biotech Fund targeting Series B+ growth capital for scaling ventures',
        'Tab Trends - Trend 5 Name': 'AI-Driven Drug Discovery',
        'Tab Trends - Trend 5 Point 1': 'KAUST AI labs partnering with pharma companies for molecule screening and protein folding',
        'Tab Trends - Trend 5 Point 2': 'Opportunity to leapfrog traditional R&D timelines with computational biology',
        'Tab Trends - Trend 6 Name': 'Regional Biotech Hub Positioning',
        'Tab Trends - Trend 6 Point 1': 'Target: Become MENA\'s biotech leader by 2030 with manufacturing and export capabilities',
        'Tab Trends - Trend 6 Point 2': 'Competition: Singapore ($4B VC funding) vs Saudi ($300M) – requires 10x increase in private capital',
        'Tab Trends - Where To Start 1': 'Entrepreneurs: Focus on biosimilars and halal-certified biologics with clear market demand',
        'Tab Trends - Where To Start 2': 'Researchers: Partner with KAIMRC on genomic medicine for Arab population diseases',
        'Tab Trends - Where To Start 3': 'Investors: Target fill-finish facilities and downstream biomanufacturing infrastructure',

        // ===== TABS - INCENTIVES =====
        'Tab Incentives - Section 1 Title': 'Financial Incentives',
        'Tab Incentives - Section 1 Item 1 Subtitle': 'Funding & Grants',
        'Tab Incentives - Section 1 Item 1 Point 1': 'Saudi Venture Capital: Co-investment matching up to $10M for biotech Series A/B rounds',
        'Tab Incentives - Section 1 Item 1 Point 2': 'R&D Tax Incentives: 50% tax deduction for private R&D investments in biologics',
        'Tab Incentives - Section 1 Item 1 Point 3': 'PIF Biotech Fund: $500M dedicated fund for scaling biotech manufacturing ventures',
        'Tab Incentives - Section 1 Item 2 Subtitle': 'Manufacturing Support',
        'Tab Incentives - Section 1 Item 2 Point 1': 'SPARK Facilities: Subsidized GMP manufacturing space at 30% below market rates',
        'Tab Incentives - Section 1 Item 2 Point 2': 'Equipment Grants: Up to $5M for bioreactor and fill-finish equipment purchases',
        'Tab Incentives - Section 2 Title': 'Regulatory Fast-Track',
        'Tab Incentives - Section 2 Point 1': 'Priority Review: 12-month approval timeline for locally-manufactured biologics (vs. 18 months)',
        'Tab Incentives - Section 2 Point 2': 'Rolling Submissions: Submit clinical trial data as available instead of waiting for completion',
        'Tab Incentives - Section 2 Point 3': 'SFDA ML4 Status: International regulatory recognition enables export to 100+ countries',
        'Tab Incentives - Section 3 Title': 'Talent & Training',
        'Tab Incentives - Section 3 Point 1': 'TAVSEEN Program: Government covers 50% of biotech specialist salaries for 2 years',
        'Tab Incentives - Section 3 Point 2': 'Golden Visas: Fast-track residency for biotech PhDs and executives',
        'Tab Incentives - Section 4 Title': 'Market Access',
        'Tab Incentives - Section 4 Point 1': 'Local Content Mandates: 30% procurement preference for Saudi-made biologics by 2030',
        'Tab Incentives - Section 4 Point 2': 'Export Subsidies: 20% cost support for halal-certified biologics entering global markets',
        'Tab Incentives - Section 5 Title': 'Infrastructure Access',
        'Tab Incentives - Section 5 Point 1': 'Free Trade Zones: Customs waivers for biotech special economic zones',
        'Tab Incentives - Section 5 Point 2': 'Genomic Data Access: Subsidized licensing of KAIMRC\'s 2M+ Arab genome database',

        // ===== VALUE CHAIN - IMPORT =====
        'Value Chain Import - Title': 'Import Dependency Analysis',
        'Value Chain Import - Top Category 1 Name': 'Monoclonal Antibodies (mAbs)',
        'Value Chain Import - Top Category 1 Share': '35%',
        'Value Chain Import - Top Category 1 Suppliers': 'USA (Pfizer, Merck), Switzerland (Roche)',
        'Value Chain Import - Top Category 2 Name': 'Vaccines (mRNA & Traditional)',
        'Value Chain Import - Top Category 2 Share': '25%',
        'Value Chain Import - Top Category 2 Suppliers': 'Belgium (Pfizer), USA (Moderna), India (Serum Institute)',
        'Value Chain Import - Top Category 3 Name': 'Insulin & Diabetes Biologics',
        'Value Chain Import - Top Category 3 Share': '18%',
        'Value Chain Import - Top Category 3 Suppliers': 'Denmark (Novo Nordisk), USA (Eli Lilly)',
        'Value Chain Import - Top Category 4 Name': 'Biosimilars',
        'Value Chain Import - Top Category 4 Share': '12%',
        'Value Chain Import - Top Category 4 Suppliers': 'South Korea (Samsung Biologics), India (Biocon)',
        'Value Chain Import - Top Category 5 Name': 'CAR-T & Gene Therapies',
        'Value Chain Import - Top Category 5 Share': '10%',
        'Value Chain Import - Top Category 5 Suppliers': 'USA (Gilead, Novartis), Switzerland (Roche)',
        'Value Chain Import - Dominant Supplier 1': 'USA (40%): Pfizer, Merck, Gilead – Dominates mAbs and gene therapies',
        'Value Chain Import - Dominant Supplier 2': 'EU (35%): Roche, Novo Nordisk, Sanofi – Strong in insulin and vaccines',
        'Value Chain Import - Dominant Supplier 3': 'Asia (20%): Serum Institute (India), Samsung Biologics (Korea) – Biosimilars and generics',
        'Value Chain Import - Critical Dependency 1': 'mRNA Vaccines: 100% imported from Belgium/USA – no local capacity despite SPARK investments',
        'Value Chain Import - Critical Dependency 2': 'CAR-T Therapies: Entirely from USA/EU at $500K+/dose – zero local production',
        'Value Chain Import - Import Reduction Plan Text': 'Target: Cut biologics imports from 70% to 50% by 2030 via:',
        'Value Chain Import - Import Reduction Point 1': 'Local mRNA Production: SPARK\'s BioNTech partnership aims for 50M vaccine doses/year by 2026',
        'Value Chain Import - Import Reduction Point 2': 'Halal Biotech Partnerships: Joint ventures with Malaysia/Indonesia for Islamic-compliant biologics',

        // ===== VALUE CHAIN - RESEARCH & DISCOVERY =====
        'Value Chain Research - Title': 'Research & Discovery',
        'Value Chain Research - Summary': 'Strong genomic data foundation (60% of population carries diabetes/genetic markers) but lags in translational research. Only 3% of VC funding targets early-stage biotech discoveries.',
        'Value Chain Research - Product/Service Needed 1': 'AI-driven drug discovery platforms for Arab genome-specific molecule screening',
        'Value Chain Research - Product/Service Needed 2': 'Shared GLP-certified laboratory infrastructure for preclinical testing',
        'Value Chain Research - Key Player 1': 'KAIMRC (King Abdullah International Medical Research Center) – 2M+ genome database',
        'Value Chain Research - Key Player 2': 'KAUST AI Initiative – Computational biology and protein folding research',
        'Value Chain Research - Trends': 'Halal-compliant R&D methodologies, AI-powered leapfrogging to bypass traditional discovery timelines',
        'Value Chain Research - Challenges': 'Weak academia-industry collaboration links, extremely low private sector R&D investment',
        'Value Chain Research - Risk Rating': 'High (4/5)',
        'Value Chain Research - Policy Recommendation 1': 'Tax credits (50%+) for private sector partnerships with universities on biologics R&D',
        'Value Chain Research - Policy Recommendation 2': 'Mandate genomic and clinical data sharing from public hospitals for research purposes',

        // ===== VALUE CHAIN - DEVELOPMENT =====
        'Value Chain Development - Title': 'Clinical Development & Trials',
        'Value Chain Development - Summary': 'SFDA\'s WHO ML4 status accelerates regulatory approvals, but only 5% of clinical trials target Arab-specific diseases despite high prevalence rates.',
        'Value Chain Development - Product/Service Needed 1': 'Local CROs (Contract Research Organizations) specialized in MENA patient recruitment',
        'Value Chain Development - Product/Service Needed 2': 'GMP-certified clinical trial sites with specialized facilities for biologics testing',
        'Value Chain Development - Key Player 1': 'SFDA (Saudi Food & Drug Authority) – WHO ML4 fast-track approval pathway',
        'Value Chain Development - Key Player 2': 'Cigalah-Medgulf – Saudi CRO with multi-site clinical trial management',
        'Value Chain Development - Trends': 'Decentralized clinical trials (DCTs) using remote monitoring, MERS and diabetes-focused therapeutic development',
        'Value Chain Development - Challenges': 'Slow patient recruitment due to limited awareness, severe lack of specialized trial infrastructure',
        'Value Chain Development - Risk Rating': 'Medium (3/5)',
        'Value Chain Development - Policy Recommendation 1': 'Government subsidies (30-50%) for clinical trials targeting high-burden local diseases',
        'Value Chain Development - Policy Recommendation 2': 'Streamline ethics committee approvals to match 60-day international standards',

        // ===== VALUE CHAIN - MANUFACTURING =====
        'Value Chain Manufacturing - Title': 'Biomanufacturing',
        'Value Chain Manufacturing - Summary': '90% of biomanufacturing raw materials and consumables are imported, creating supply chain vulnerabilities. However, major mRNA and biosimilar facilities are under construction.',
        'Value Chain Manufacturing - Product/Service Needed 1': 'Local fill-finish capacity for sterile biologics packaging (currently 90% outsourced)',
        'Value Chain Manufacturing - Product/Service Needed 2': 'Halal-certified cell culture media and bioreactor consumables production',
        'Value Chain Manufacturing - Key Player 1': 'BioNTech-SPARK Partnership – $1.2B mRNA vaccine manufacturing facility in Riyadh',
        'Value Chain Manufacturing - Key Player 2': 'SPARK Biocity – Shared GMP manufacturing infrastructure for startups and scale-ups',
        'Value Chain Manufacturing - Trends': 'Continuous bioprocessing adoption, positioning for MENA regional vaccine export hub',
        'Value Chain Manufacturing - Challenges': 'Extremely high capital expenditure requirements ($500M+ for GMP facilities), supply chain fragility for critical materials',
        'Value Chain Manufacturing - Risk Rating': 'Critical (5/5)',
        'Value Chain Manufacturing - Policy Recommendation 1': 'Local content mandates requiring 30% Saudi-made components in biologics by 2030',
        'Value Chain Manufacturing - Policy Recommendation 2': 'Co-investment funds providing 50% matching capital for modular biomanufacturing factories',

        // ===== VALUE CHAIN - COMMERCIALIZATION =====
        'Value Chain Commercialization - Title': 'Market Access & Commercialization',
        'Value Chain Commercialization - Summary': 'Saudi hospitals and pharmacies currently prefer established imported biologics (70% market share). However, halal-certified biologics could unlock access to $125B global Islamic bioeconomy market.',
        'Value Chain Commercialization - Product/Service Needed 1': 'Market access consultants with expertise in Saudi procurement and reimbursement systems',
        'Value Chain Commercialization - Product/Service Needed 2': 'Halal certification agencies specialized in biologics manufacturing compliance',
        'Value Chain Commercialization - Key Player 1': 'Saudi Pharma – Largest domestic pharmaceutical distributor with national hospital network',
        'Value Chain Commercialization - Key Player 2': 'Halal Development Corporation – Governs Islamic-compliant certification standards',
        'Value Chain Commercialization - Trends': 'Islamic bioeconomy expansion, shift toward value-based pricing models for high-cost biologics',
        'Value Chain Commercialization - Challenges': 'Government price controls limiting margins, insurer resistance to premium pricing for local products',
        'Value Chain Commercialization - Risk Rating': 'Medium-High (4/5)',
        'Value Chain Commercialization - Policy Recommendation 1': 'Procurement quotas mandating 30-50% local biologics purchases by public hospitals',
        'Value Chain Commercialization - Policy Recommendation 2': 'Export subsidies (20% cost support) for halal-certified biologics entering global markets',

        // ===== SUPPORT ECOSYSTEM - GENERAL =====
        'Support Ecosystem - Summary': 'Saudi Arabia needs 10,000+ biotech specialists by 2030. PIF\'s $500M dedicated biotech fund targets scaling ventures, but early-stage funding remains scarce.',
        'Support Ecosystem - Product/Service Needed 1': 'Bioprocessing and biomanufacturing training programs with GMP certification pathways',
        'Support Ecosystem - Product/Service Needed 2': 'Venture capital focused on Series B+ growth rounds for biotech scale-ups',
        'Support Ecosystem - Key Player 1': 'PIF (Public Investment Fund) – $500M biotech fund for manufacturing and commercialization',
        'Support Ecosystem - Key Player 2': 'TAVSEEN – Wage support program covering 50% of specialist salaries for 2 years',
        'Support Ecosystem - Trends': 'NEOM positioning as dedicated biotech innovation hub, AI-focused talent pipeline development',
        'Support Ecosystem - Challenges': 'Severe brain drain (40% of graduates work abroad), risk-averse investor culture favoring late-stage deals',
        'Support Ecosystem - Risk Rating': 'High (4/5)',
        'Support Ecosystem - Policy Recommendation 1': 'Golden visa fast-track program for biotech PhDs and C-suite executives',
        'Support Ecosystem - Policy Recommendation 2': 'Matching grants (1:1 ratio) incentivizing investor-startup early-stage partnerships',

        // ===== SUPPORT ECOSYSTEM - TALENT =====
        'Support Ecosystem Talent - Title': 'Talent Development & Workforce',
        'Support Ecosystem Talent - Critical Shortage 1': 'Bioprocessing Engineers: Less than 100 locally trained vs. 500+ needed by 2028',
        'Support Ecosystem Talent - Critical Shortage 2': 'Regulatory Affairs Specialists: 90% currently hired from abroad due to lack of local expertise',
        'Support Ecosystem Talent - Critical Shortage 3': 'AI/Bioinformatics Experts: Extremely high demand for computational biology and genomics analysis',
        'Support Ecosystem Talent - University 1 Name': 'KAUST (King Abdullah University of Science & Technology)',
        'Support Ecosystem Talent - University 1 Program 1': 'MS in Bioengineering (focus on synthetic biology and tissue engineering)',
        'Support Ecosystem Talent - University 1 Program 2': 'PhD in Computational Biology & Bioinformatics',
        'Support Ecosystem Talent - University 2 Name': 'King Saud University',
        'Support Ecosystem Talent - University 2 Program 1': 'Bachelor\'s degree in Biotechnology',
        'Support Ecosystem Talent - University 2 Program 2': 'MS in Pharmaceutical Sciences',
        'Support Ecosystem Talent - University 3 Name': 'King Abdulaziz University',
        'Support Ecosystem Talent - University 3 Program 1': 'Bachelor\'s in Biochemistry',
        'Support Ecosystem Talent - University 3 Program 2': 'MS in Medical Biotechnology',
        'Support Ecosystem Talent - Government Initiative 1': 'TAVSEEN Wage Support: Government covers 50% of biotech specialist salaries for first 2 years',
        'Support Ecosystem Talent - Government Initiative 2': 'Saudi NIH Fellowships: Subsidized clinical research training with international placements',
        'Support Ecosystem Talent - Trend/Challenge 1': 'Brain Drain Crisis: 40% of biotech graduates pursue careers abroad in USA/EU due to higher salaries',
        'Support Ecosystem Talent - Trend/Challenge 2': 'Upskilling Gap: 70% of current pharmaceutical workforce needs advanced GMP and biologics training',

        // ===== SUPPORT ECOSYSTEM - FUNDING =====
        'Support Ecosystem Funding - Title': 'Investment & Funding Landscape',
        'Support Ecosystem Funding - Investment Landscape Summary': 'Saudi biotech funding reached $300M in 2023, but it remains heavily concentrated in late-stage manufacturing ventures, leaving a critical early-stage gap.',
        'Support Ecosystem Funding - Key Metric 1': 'Early-Stage Gap: Only 3% of total VC funding allocated to seed and Series A biotech rounds',
        'Support Ecosystem Funding - Key Metric 2': 'Growth Capital: $500M PIF Biotech Fund specifically targets Series B+ expansion and manufacturing scale-up',
        'Support Ecosystem Funding - Key Metric 3': 'Exit Challenge: Zero biotech IPOs on Tadawul stock exchange in past 5 years',
        'Support Ecosystem Funding - Source Group 1 Name': 'Government Sources',
        'Support Ecosystem Funding - Source Group 1 Entity 1': 'PIF Biotech Fund: $500M dedicated fund for manufacturing and commercialization investments',
        'Support Ecosystem Funding - Source Group 1 Entity 2': 'KAUST Innovation Fund: Seed funding (up to $500K) for university spin-outs',
        'Support Ecosystem Funding - Source Group 1 Entity 3': 'SFDA Innovation Grants: R&D grants for regulatory-approved clinical trials',
        'Support Ecosystem Funding - Source Group 2 Name': 'Private Capital',
        'Support Ecosystem Funding - Source Group 2 Entity 1': 'Saudi Venture Capital Company: Co-investment matching for biotech Series A/B rounds',
        'Support Ecosystem Funding - Source Group 2 Entity 2': 'Impact46: Early-stage VC targeting health tech and diagnostics',
        'Support Ecosystem Funding - Source Group 2 Entity 3': 'International Partners: Strategic investments from Novo Holdings, Johnson & Johnson Innovation',
        'Support Ecosystem Funding - Trend/Gap 1': 'Manufacturing Bias: 70% of funding flows to manufacturing/commercialization vs. R&D and discovery',
        'Support Ecosystem Funding - Trend/Gap 2': 'Grant Dependency: 3:1 ratio of government grants to risk capital – stifles entrepreneurial scaling',
        'Support Ecosystem Funding - Trend/Gap 3': 'Valley of Death: Critical funding gap in $2M-$10M range for translational research scaling',

        // ===== SUPPORT ECOSYSTEM - SUPPLY CHAIN =====
        'Support Ecosystem Supply Chain - Title': 'Supply Chain & Logistics',
        'Support Ecosystem Supply Chain - Critical Gap 1': '90% of biomanufacturing supplies (cell culture media, filters, single-use systems) are imported',
        'Support Ecosystem Supply Chain - Critical Gap 2': 'Average 14-day customs clearance for biologics imports (vs. 3-5 days in Singapore)',
        'Support Ecosystem Supply Chain - Critical Gap 3': 'Only 3 certified -80°C ultra-cold chain storage facilities nationwide for mRNA and cell therapies',
        'Support Ecosystem Supply Chain - Localization Opp 1 Category': 'Cell Culture Media',
        'Support Ecosystem Supply Chain - Localization Opp 1 Details': '70% currently imported; $300M annual market opportunity for local production',
        'Support Ecosystem Supply Chain - Localization Opp 1 Target Year': '2026-2028',
        'Support Ecosystem Supply Chain - Localization Opp 2 Category': 'Fill-Finish Capacity',
        'Support Ecosystem Supply Chain - Localization Opp 2 Details': '90% of sterile filling outsourced abroad; Vision 2030 targets 50% localization',
        'Support Ecosystem Supply Chain - Localization Opp 2 Target Year': '2025-2027',
        'Support Ecosystem Supply Chain - Localization Opp 3 Category': 'Bioreactor Consumables',
        'Support Ecosystem Supply Chain - Localization Opp 3 Details': 'Single-use systems and filters – $150M import market with halal certification advantage',
        'Support Ecosystem Supply Chain - Localization Opp 3 Target Year': '2027-2029',
        'Support Ecosystem Supply Chain - Highest Risk 1': 'Single-source APIs for biosimilar production – extremely vulnerable to supply disruptions',
        'Support Ecosystem Supply Chain - Highest Risk 2': 'Limited sterile filling capacity creates production bottleneck for all biologics',
        'Support Ecosystem Supply Chain - Emerging Risk 1': 'Competition for halal-certified raw materials as Malaysia/Indonesia scale up biotech production',
        'Support Ecosystem Supply Chain - Emerging Risk 2': 'Customs bottlenecks during Hajj/Ramadan seasons cause critical delays for time-sensitive biologics',
        'Support Ecosystem Supply Chain - Strategic Initiative 1': 'SPARK Supply Chain Hub: Regional distribution center for MENA biologics exports',
        'Support Ecosystem Supply Chain - Strategic Initiative 2': 'Digital Supply Chain Monitoring: SFDA blockchain pilot for track-and-trace launching 2025',
        'Support Ecosystem Supply Chain - Strategic Initiative 3': 'Saudi Logistics Special Zone: Dedicated biotech warehousing with fast-track customs processing',
        // ===== SUB-INDUSTRY 1 - BASIC INFO =====
        'Sub-Industry 1 Name': 'Biotechnology & Biologics',
        'Sub-Industry 1 Description': 'Biological pharmaceutical products including vaccines, mAbs, biosimilars, and gene therapies',
        'Sub-Industry 1 Summary': 'Saudi Arabia is building a biotech future with $1B+ mRNA vaccine facilities, unique Arab genome advantages for precision medicine, and Vision 2030\'s strategic push for halal-certified biologics targeting $125B global market.',
        'Sub-Industry 1 Growth Rate (%)': '15.5',
        'Sub-Industry 1 Market Size': '$4.2B',
        'Sub-Industry 1 Investment Opportunities': '28',
        'Sub-Industry 1 Key Metrics - Employment': '8,500+',
        'Sub-Industry 1 Key Metrics - Exports': '$450M',
        'Sub-Industry 1 Key Metrics - Companies': '45',
        'Sub-Industry 1 Growth Data 2020': '1.2',
        'Sub-Industry 1 Growth Data 2021': '1.6',
        'Sub-Industry 1 Growth Data 2022': '2.3',
        'Sub-Industry 1 Growth Data 2023': '3.4',
        'Sub-Industry 1 Growth Data 2024': '4.2'
      }];

      const wsExample = XLSX.utils.json_to_sheet(examplePharm);
      wsExample['!cols'] = examplePharm[0] ? Object.keys(examplePharm[0]).map(() => ({ wch: 35 })) : [];

      // Apply professional styling to example sheet headers (same as template)
      const exampleRange = XLSX.utils.decode_range(wsExample['!ref'] || 'A1');
      for (let col = exampleRange.s.c; col <= exampleRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        const colName = examplePharm[0] ? Object.keys(examplePharm[0])[col] : '';

        if (!wsExample[cellAddress]) continue;

        // Color-code by section (same logic as template)
        let headerColor = "2563EB"; // Default blue
        if (colName.startsWith('Tab Market Overview')) headerColor = "059669"; // Green
        else if (colName.startsWith('Tab Legal') || colName.startsWith('Tab Infrastructure')) headerColor = "DC2626"; // Red
        else if (colName.startsWith('Tab Trends')) headerColor = "7C3AED"; // Purple
        else if (colName.startsWith('Tab Incentives')) headerColor = "EA580C"; // Orange
        else if (colName.startsWith('Value Chain')) headerColor = "0891B2"; // Cyan
        else if (colName.startsWith('Support Ecosystem')) headerColor = "DB2777"; // Pink
        else if (colName.startsWith('Sub-Industry')) headerColor = "65A30D"; // Lime

        wsExample[cellAddress].s = {
          font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: headerColor } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Freeze first row for example sheet
      wsExample['!freeze'] = { xSplit: 0, ySplit: 1 };

      XLSX.utils.book_append_sheet(wb, wsExample, '✅ Example - Pharmaceutical');

      // Write file
      XLSX.writeFile(wb, `Industry_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`);

      setSnackbar({
        open: true,
        message: 'Template downloaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download template',
        severity: 'error'
      });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          // Read the template sheet
          const sheetName = workbook.SheetNames.find((name: string) =>
            name.toLowerCase().includes('template') || name.toLowerCase().includes('industries')
          ) || workbook.SheetNames[0];

          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            throw new Error('No data found in the Excel file');
          }

          // Send to backend
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch('/api/admin/industries/bulk-upload', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.error || 'Upload failed');
          }

          setSnackbar({
            open: true,
            message: `Successfully imported ${result.data.created} industries`,
            severity: 'success'
          });

          // Refresh industries list
          const updatedIndustries = await industryApi.getAllIndustriesAdmin({ includeSubIndustries: true });
          setIndustries(updatedIndustries);
        } catch (error) {
          console.error('Error processing file:', error);
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'Failed to process file',
            severity: 'error'
          });
        } finally {
          setUploading(false);
          // Reset file input
          event.target.value = '';
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload file',
        severity: 'error'
      });
      setUploading(false);
    }
  };

  // Statistics data
  const stats: StatCard[] = [
    {
      title: 'Total Industries',
      value: industries.length.toString(),
      change: '+2 this quarter',
      icon: <BusinessIcon />,
      color: 'primary',
      description: 'Active industry sectors'
    },
    {
      title: 'Sub-Industries',
      value: industries.reduce((sum, ind) => sum + (ind.subIndustries?.length || 0), 0).toString(),
      change: '+8 new segments',
      icon: <CategoryIcon />,
      color: 'secondary',
      description: 'Specialized segments'
    },
    {
      title: 'Localization Focus',
      value: industries.filter(ind => ind.category === 'Localization').length.toString(),
      change: 'High priority',
      icon: <TrendingUpIcon />,
      color: 'success',
      description: 'Vision 2030 aligned'
    },
    {
      title: 'Active Industries',
      value: industries.filter(ind => ind.isActive !== false).length.toString(),
      change: '100% operational',
      icon: <VisibilityIcon />,
      color: 'info',
      description: 'Currently active'
    }
  ];

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
            Loading industries...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(
            theme.palette.secondary.main,
            0.8
          )})`,
          position: 'relative',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
            transform: 'translateX(20%)'
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 48 }} />
                Industry Management
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                Manage and configure industry data and information
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 600 }}>
                Edit industry profiles, manage sub-industries, and update market data with comprehensive tools
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                startIcon={<TemplateIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  borderColor: 'white',
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9), borderColor: 'white' },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Download Template
              </Button>
              <Button
                variant="outlined"
                component="label"
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.secondary.main,
                  borderColor: 'white',
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9), borderColor: 'white' },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Excel'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                />
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/industry-details/new')}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.9) },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Add New Industry
              </Button>
            </Box>
          </Box>
        </Box>
      </MotionPaper>

      {/* Statistics Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4, boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.05)}, ${alpha(
                  theme.palette[stat.color].light,
                  0.02
                )})`,
                border: `1px solid ${alpha(theme.palette[stat.color].main, 0.1)}`,
                height: '100%'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette[stat.color].main, 0.1),
                      color: `${stat.color}.main`,
                      mr: 2
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={`${stat.color}.main`}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {stat.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.description}
                </Typography>
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {/* Search and Filter */}
      <MotionPaper 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: alpha(theme.palette.background.paper, 0.7),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon />
          Search & Filter Industries
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search industries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredIndustries.length} results
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHidden}
                    onChange={(e) => setShowHidden(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Show hidden
                  </Typography>
                }
              />
            </Box>
          </Grid>
        </Grid>
      </MotionPaper>

      {/* Industries Grid */}
      <Grid container spacing={3}>
        {filteredIndustries.map((industry, index) => (
          <Grid item xs={12} sm={6} lg={4} key={industry.id}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4, boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }}
              sx={{
                borderRadius: 3,
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                border: industry.isActive !== false
                  ? `2px solid ${alpha(theme.palette.success.main, 0.2)}`
                  : `1px solid ${theme.palette.divider}`,
                background: industry.isActive !== false
                  ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)}, ${alpha(
                      theme.palette.success.light,
                      0.01
                    )})`
                  : alpha(theme.palette.action.disabled, 0.05)
              }}
            >
              {/* Status indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                  borderStyle: 'solid',
                  borderWidth: '0 40px 40px 0',
                  borderColor: `transparent ${industry.isActive !== false ? theme.palette.success.main : theme.palette.grey[400]} transparent transparent`,
                  zIndex: 1
                }}
              />

              <CardActionArea
                onClick={() => handleIndustryClick(industry.id)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <CardContent sx={{ p: 3, width: '100%', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: 'primary.main',
                        width: 56,
                        height: 56,
                        fontSize: '1.2rem',
                        fontWeight: 600
                      }}
                    >
                      {industry.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={600} noWrap gutterBottom>
                        {industry.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          label={industry.category}
                          color={getCategoryColor(industry.category)}
                          size="small"
                          sx={{ borderRadius: 1 }}
                        />
                        <Chip
                          label={getStatusBadge(industry.status).label}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            bgcolor: getStatusBadge(industry.status).bgcolor,
                            color: getStatusBadge(industry.status).textColor,
                            fontWeight: 600
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {industry.id}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {/* Submit for Review - Only for draft status */}
                      {industry.status === 'draft' && (
                        <Tooltip title="Submit for Review">
                          <IconButton
                            size="small"
                            onClick={(e) => handleSubmitForReview(industry.id, e)}
                            sx={{
                              color: 'info.main',
                              bgcolor: alpha(theme.palette.info.main, 0.1)
                            }}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Approve - Only for pending_review status and super admins */}
                      {isSuperAdmin && industry.status === 'pending_review' && (
                        <Tooltip title="Approve & Publish">
                          <IconButton
                            size="small"
                            onClick={(e) => handleApprove(industry.id, e)}
                            sx={{
                              color: 'success.main',
                              bgcolor: alpha(theme.palette.success.main, 0.1)
                            }}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Hide - Only for published status and super admins */}
                      {isSuperAdmin && industry.status === 'published' && (
                        <Tooltip title="Hide Industry">
                          <IconButton
                            size="small"
                            onClick={(e) => handleHide(industry.id, e)}
                            sx={{
                              color: 'warning.main',
                              bgcolor: alpha(theme.palette.warning.main, 0.1)
                            }}
                          >
                            <HideIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Permanent Delete - Super admins only */}
                      {isSuperAdmin && (
                        <Tooltip title="Permanently Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handlePermanentDelete(industry.id, e)}
                            sx={{
                              color: 'error.main',
                              bgcolor: alpha(theme.palette.error.main, 0.1)
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Edit - Always available */}
                      <Tooltip title="Edit Industry">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIndustryClick(industry.id);
                          }}
                          sx={{
                            color: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                    {industry.description && industry.description.length > 120
                      ? `${industry.description.substring(0, 120)}...`
                      : industry.description || 'No description available'
                    }
                  </Typography>

                  {/* Key Metrics */}
                  <Box sx={{ mt: 'auto' }}>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Market Size</Typography>
                        <Typography variant="body2" fontWeight={600}>{industry.marketSize || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Growth Rate</Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {industry.growthRate !== undefined ? `${industry.growthRate}%` : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Sub-industries indicator */}
                    {industry.hasSubIndustries && industry.subIndustries && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {industry.subIndustries.length} sub-industries
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {filteredIndustries.length === 0 && (
        <MotionPaper 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          sx={{ p: 6, textAlign: 'center', mt: 4, borderRadius: 3 }}
        >
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No industries found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchTerm || filterCategory !== 'all' 
              ? 'Try adjusting your search criteria or filters'
              : 'No industries available in the system'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/industry-details/new')}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            Add First Industry
          </Button>
        </MotionPaper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default IndustryManagement;